import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FmDocument, SmartPortalProcessError } from '@core/api';
import { FileService } from '@core/services/file-service/file.service';
import { MandantConfigService } from '@core/services/mandant-config-service/mandant-config.service';
import { SpeedikonService } from '@core/services/speedikon/speedikon.service';
import { MtxGrid, MtxGridColumn } from '@ng-matero/extensions/grid';
import { TranslateModule } from '@ngx-translate/core';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { TableBaseComponent } from '@shared/components/table-base/table-base';
import moment from 'moment';
import { of } from 'rxjs';
import { OverrideModule } from '../../../../override/override.module';
import { SpeedikonUploadModalComponent } from './speedikon-upload-modal/speedikon-upload-modal.component';

interface DocumentRow extends FmDocument {
  downloadLoading: boolean;
  archiveToImageMasterLoading: boolean;
  actionsDisabled: boolean;
}

interface IMtxGridColumn extends MtxGridColumn {
  canUpdate?: boolean;
}

@Component({
  selector: 'app-speedikon-upload-docs',
  templateUrl: './speedikon-upload-docs.component.html',
  styleUrls: ['./speedikon-upload-docs.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    SmartusComponentsModule,
    MtxGrid,
    OverrideModule,
  ],
  providers: [FileService, MandantConfigService],
})
export class SpeedikonUploadDocsComponent
  extends TableBaseComponent
  implements OnInit, OnChanges
{
  readonly mandantConfigService = inject(MandantConfigService);
  readonly fileService = inject(FileService);
  readonly dialog = inject(MatDialog);
  readonly speedikonService = inject(SpeedikonService);

  SpeedikonArchObjId = computed(() =>
    this.speedikonService.savedArchObjIdSignal()
  );

  @Input() documents: FmDocument[];
  @Input() canDeleteDocument = false;

  @ViewChild('documentActionTemplate', { static: true })
  documentActionTemplateRef!: TemplateRef<void>;

  override columns: IMtxGridColumn[] = [];
  oldVersionColumns: IMtxGridColumn[] = [];
  override hasMultiDownload =
    this.mandantConfigService.mandant.hasMultiDownload ?? false;

  override isLoading: boolean;

  deleteErrorMessage = this.translateService.instant(
    'documents.list.delete_error_msg.default'
  );

  localDocuments: FmDocument[] = [];

  ngOnInit(): void {
    this.initColumns();
    this.oldVersionsColumns();
    this.cd.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['documents']) {
      this.localDocuments = [...this.documents];
      this.cd.detectChanges();
    }
    this.getData();
    this.initColumns();
  }

  oldVersionsColumns(): void {
    this.oldVersionColumns = [
      {
        field: 'name',
      },
      {
        field: 'changeDate',
        width: '200px',
        formatter: (data: FmDocument) =>
          data.changeDate
            ? moment.utc(data.changeDate).local().format('DD.MM.YYYY, HH:mm:ss')
            : '--',
      },
      {
        field: 'action',
        width: '60px',
        cellTemplate: this.documentActionTemplateRef,
        class: 'padding-right padding-left',
      },
    ];
  }

  initColumns(): void {
    this.columns = [
      {
        field: 'showExpand',
        showExpand: true,
        width: '90px',
      },
      {
        header: this.translateService.instant('speedikon.table.name'),
        field: 'filename',
        sortable: true,
      },
      {
        header: this.translateService.instant('speedikon.table.date'),
        field: 'changeDate',
        width: '200px',
        sortable: true,
        formatter: (data: FmDocument) =>
          data.changeDate
            ? moment.utc(data.changeDate).local().format('DD.MM.YYYY, HH:mm:ss')
            : '--',
      },
      {
        field: 'action',
        width: '60px',
        cellTemplate: this.documentActionTemplateRef,
        canUpdate: true,
        class: 'no-padding-right',
      },
    ];
  }

  override getTableData$ = () => {
    return of({ entites: this.documents, totalCount: this.documents.length });
  };

  override async downloadDocument(event: Event, row: DocumentRow) {
    if (!row.blobFilename) {
      this.toastr.error(
        this.translateService.instant('detailFields.fileDownload.warn')
      );
      return;
    }
    event.stopPropagation();
    row.actionsDisabled = true;
    row.downloadLoading = true;
    this.isLoading = true;
    this.cd.detectChanges();

    this.pushSubscription(
      this.fileService
        .downloadFile(row.blobFilename, row.contentType)
        .subscribe({
          next: res => {
            const fileResponse = res;
            row.actionsDisabled = false;
            row.downloadLoading = false;
            this.isLoading = false;
            this.cd.detectChanges();

            if (fileResponse !== null) {
              this.fileService.saveFile(fileResponse, row.name);
            }
          },
          error: error => {
            if (
              error.errorCode ===
              SmartPortalProcessError.FILESIZE_DOWNLOAD_LIMIT
            ) {
              this.toastr.error(
                this.translateService.instant(
                  'detailFields.fileDownload.filesize_download_limit'
                )
              );
            } else {
              this.toastr.error(
                this.translateService.instant('detailFields.fileDownload.warn')
              );
            }
            row.actionsDisabled = false;
            row.downloadLoading = false;
            this.isLoading = false;
            this.cd.detectChanges();
          },
        })
    );
  }

  override deleteDocument(row: DocumentRow) {
    const dialogRef = this.dialog.open(DialogComponent, {
      panelClass: 'smartportal-dialog',
      width: '500px',
      data: {
        type: 'okDismissButton',
        title: this.translateService.instant(
          'speedikon.confirmationDialog.delete.title'
        ),
        message: this.translateService.instant(
          'speedikon.confirmationDialog.delete.message',
          {
            filename: row.filename,
          }
        ),
        okButtonText: this.translateService.instant('shared.confirm'),
        dismissButtonText: this.translateService.instant('shared.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe({
      next: result => {
        if (result) {
          this.isLoading = true;
          this.cd.detectChanges();

          this.fileClient.setFileDeleted(row.documentId).subscribe({
            next: () => {
              this.toastr.success(
                this.translateService.instant(
                  'documents.list.delete_success_msg'
                )
              );
              row.deleted = true;
              this.getSpeedikonBuildingDocuments();
              this.isLoading = false;
              this.cd.detectChanges();
            },
            error: err => {
              if (
                err.errorCode ===
                SmartPortalProcessError.FILE_DELETION_MISSING_AUTHORIZATION
              ) {
                this.deleteErrorMessage = this.translateService.instant(
                  'documents.list.delete_error_msg.no_rights'
                );
              }
              this.isLoading = false;
              this.toastr.error(
                this.deleteErrorMessage,
                this.translateService.instant('alert.error')
              );
            },
          });
        }
      },
    });
  }

  openUpdateDocumentModal(row) {
    const dialogRef = this.dialog.open(SpeedikonUploadModalComponent, {
      minWidth: '40vw',
      minHeight: '40vh',
      data: {
        document: row,
      },
    });

    dialogRef.componentInstance.uploadCompleted.subscribe(() => {
      this.refreshData();
    });

    dialogRef.afterClosed().subscribe(() => {
      this.cd.detectChanges();
    });
  }

  refreshData() {
    this.isLoading = true;
    this.cd.detectChanges();
    setTimeout(() => {
      this.getSpeedikonBuildingDocuments();
    }, 4000);
    this.getData();
    this.initColumns();
    this.cd.detectChanges();
  }

  getSpeedikonBuildingDocuments() {
    return this.speedikonService
      .getSpeedikonDocuments(this.SpeedikonArchObjId())
      .subscribe({
        next: (documents: FmDocument[]) => {
          this.documents = documents;
          this.localDocuments = [...documents];
          this.cd.detectChanges();
        },
      });
  }
}
